import { useContext, useEffect, useState } from 'react'
import type { EventTags } from '@optimizely/optimizely-sdk'

import { Container } from '@awazevr/design-system.components.container'
import { SearchControls } from '@awazevr/design-system.compositions.search-controls'
import type {
  SearchFormFields,
  SearchFormProps,
} from '@awazevr/design-system.compositions.search-form'
import {
  GoogleSearchMethods,
  useFetchDestinationForLegacyList,
} from '@awazevr/mfe-common.api.places-api'
import { SearchExperience } from '@awazevr/mfe-common.compositions.search-experience'
import { useIntlContext } from '@awazevr/mfe-common.contexts.intl'
import {
  SORT_ORDER_UPDATED,
  useSearchContextDispatch,
  useSearchContextState,
} from '@awazevr/mfe-common.contexts.search'
import { TmsContext } from '@awazevr/mfe-common.contexts.tms'
import { getExperimentVariationKey } from '@awazevr/mfe-common.lib.optimizely'

import {
  MobileStickableContent,
  MobileSticky,
} from '@/components/MobileSticky/MobileSticky'
import { StickableHeaderExtended } from '@/components/MobileSticky/StickableHeaderExtended'
import { OPTIMIZELY_EVENTS, OPTIMIZELY_FLAGS_ENUM } from '@/constants'
import { useAppBrandConfig, useAppConfig } from '@/context/app-config-provider'
import { useOptimizelyDecisionsContext } from '@/context/optimizely-decisions-context'
import { buildExpandedDestinationLabel } from '@/experiment/AS_2729_Properties_Nearby'
import { useApplicationConfig } from '@/lib/applicationConfig'
import { addBoatsToDestinationList } from '@/lib/contentful/boats-option-data'
import { useDisplayContext } from '@/lib/display/displayContext'
import { Assert } from '@/utils/assert'
import { dataLayerErrorHandler } from '@/utils/dataLayerErrorHandler'
import { setDisplayModeWrapper } from '@/utils/displayModeOnClickHelper'
import { trackClientSideOptimizelyEvent } from '@/utils/optimizely/trackOptimizelyEvent'
import {
  getSortOptionsFromConfig,
  shouldDisplaySortOptions,
} from '@/utils/sortOptions/sortOptions'

import {
  getHardcodedDestinationData,
  useGoogleFallback,
} from './SearchForm.utils'

/**
 * Maps the search-experience package's semantic tracking keys onto the MFE's
 * Optimizely event keys. Keys not present here (e.g. the geolocation error keys
 * the package emits, which are already real Optimizely keys) are passed through
 * unchanged.
 */
const OPTIMIZELY_EVENT_KEY_MAP: Record<string, string> = {
  'nearby-properties-seen': OPTIMIZELY_EVENTS.NEARBY_PROPERTIES_SEEN,
  'internal-location-suggestion-shown':
    OPTIMIZELY_EVENTS.INTERNAL_LOCATION_SUGGESTION_SHOWN,
  'internal-location-suggestion-no-results':
    OPTIMIZELY_EVENTS.INTERNAL_LOCATION_SUGGESTION_NO_RESULTS,
  'internal-location-suggestion-selected':
    OPTIMIZELY_EVENTS.INTERNAL_LOCATION_SUGGESTION_SELECTED,
  'google-places-suggestion-selected':
    OPTIMIZELY_EVENTS.GOOGLE_PLACES_SUGGESTION_SELECTED,
  'google-places-suggestion-shown':
    OPTIMIZELY_EVENTS.GOOGLE_PLACES_SUGGESTION_SHOWN,
  'search-form-submitted': OPTIMIZELY_EVENTS.SEARCH_FORM_SUBMITTED,
  'nearby-properties-search-form-submitted':
    OPTIMIZELY_EVENTS.NEARBY_PROPERTIES_SEARCH_FORM_SUBMITTED,
  'smart-search-toggle-seen': OPTIMIZELY_EVENTS.SMART_SEARCH_TOGGLE_SEEN,
  'smart-search-form-seen': OPTIMIZELY_EVENTS.SMART_SEARCH_FORM_SEEN,
  'smart-search-suggestion-selected':
    OPTIMIZELY_EVENTS.SMART_SEARCH_SUGGESTION_CLICKED,
  'smart-search-submitted': OPTIMIZELY_EVENTS.SMART_SEARCH_SUBMITTED,
  'smart-search-classic-toggle-clicked':
    OPTIMIZELY_EVENTS.SMART_SEARCH_CLASSIC_TOGGLE_CLICKED,
  'smart-search-classic-submitted':
    OPTIMIZELY_EVENTS.SMART_SEARCH_CLASSIC_SUBMITTED,
  'smart-search-completed': OPTIMIZELY_EVENTS.SMART_SEARCH_RESPONSE_RECEIVED,
  'smart-search-failed': OPTIMIZELY_EVENTS.SMART_SEARCH_SUBMIT_ERROR,
  'smart-search-successful-redirect':
    OPTIMIZELY_EVENTS.SMART_SEARCH_SUCCESSFUL_REDIRECT,
}

type SearchFormComponentProps = {
  defaultConfig: SearchFormProps['defaultConfig']
  translations: SearchFormProps['translations']
  initialValues: SearchFormProps['initialValues'] & {
    destinationId?: number
  }
  locale: SearchFormProps['locale']
  openFilters: () => void
  marketCode: string
  mobileStickyMode?: boolean
  showSearchControls?: boolean
  placesConfig?: {
    useLegacyDestinationSelector: boolean
    initialParentDestinationList: string | undefined
    pageSize: number | undefined
    pageSizePopular: number | undefined
    googleFallbackMethod: GoogleSearchMethods
  }
  isGuestApp?: boolean
  /** Replaces the destination label of a map-area search. */
  mapAreaLabel?: string
}

export const SearchFormComponent: React.FC<SearchFormComponentProps> = ({
  defaultConfig,
  initialValues,
  openFilters,
  translations,
  locale,
  marketCode,
  mobileStickyMode = true,
  showSearchControls = true,
  // Fresh object literal per render so per-brand/per-request mutations below
  // (useLegacyDestinationSelector / googleFallbackMethod) can't leak across
  // renders or SSR requests via a shared module-level singleton.
  placesConfig = {
    useLegacyDestinationSelector: false,
    initialParentDestinationList: undefined,
    pageSize: 20,
    pageSizePopular: 20,
    googleFallbackMethod: undefined,
  },
  isGuestApp,
  mapAreaLabel,
}: SearchFormComponentProps) => {
  const { formatMessage } = useIntlContext()
  const {
    brand,
    clientHeader,
    searchFormBetaEnabled = false,
  } = useApplicationConfig()
  const { APP_ENV } = useAppConfig()
  const tms = useContext(TmsContext)
  const [searchFormClicked, setSearchFormClicked] = useState(false)

  const { searchCriteria, totalSelectedFilters, hasResults } =
    useSearchContextState()

  Assert.defined(searchCriteria, 'searchCriteria is not defined')
  Assert.defined(defaultConfig, 'defaultConfig is undefined')
  Assert.defined(translations, 'translations is undefined')
  Assert.defined(
    translations.destination,
    'translations.destination is undefined',
  )

  const optimizelyDecisionsContext = useOptimizelyDecisionsContext()
  const { accommodationType, sort, regionName, ne, sw, placesId } =
    searchCriteria
  const expandedPolygonVariationKey =
    optimizelyDecisionsContext.getExperimentVariationKey(
      OPTIMIZELY_FLAGS_ENUM.ONESEARCH_EXPANDED_POLYGON,
    )

  const destinationLabel =
    ne && sw && mapAreaLabel
      ? mapAreaLabel
      : buildExpandedDestinationLabel({
          regionName,
          useExpandedCoordinates: (
            searchCriteria as { useExpandedCoordinates?: boolean | string }
          ).useExpandedCoordinates,
          ne,
          sw,
          variationKey: expandedPolygonVariationKey,
        })

  const dispatch = useSearchContextDispatch()
  const isSearchFormInGuestApp = Boolean(
    isGuestApp || optimizelyDecisionsContext.isGuestApp,
  )
  const smartSearchExperimentEnabled =
    brand === 'cottages.com' &&
    !isSearchFormInGuestApp &&
    optimizelyDecisionsContext.isExperimentEnabled(
      OPTIMIZELY_FLAGS_ENUM.SEARCH_SMART_AI_SEARCH,
    )
  const smartSearchEnabled =
    smartSearchExperimentEnabled &&
    getExperimentVariationKey(
      optimizelyDecisionsContext.decisions ?? {},
      OPTIMIZELY_FLAGS_ENUM.SEARCH_SMART_AI_SEARCH,
    ) === 'variant_a'

  const appBrandConfig = useAppBrandConfig(brand)

  placesConfig.useLegacyDestinationSelector =
    appBrandConfig.useLegacyDestinationSelector

  const googleSearchMethod = useGoogleFallback(brand)

  if (googleSearchMethod) {
    placesConfig.googleFallbackMethod = googleSearchMethod
  }

  const { useLegacyDestinationSelector, googleFallbackMethod } = placesConfig

  // Optimizely XP: ACQ-1258 Use Places-API Legacy-List data
  const [fetchPlacesLegacyList, setFetchPlacesLegacyList] = useState(false)

  const placesLegacyListData = useFetchDestinationForLegacyList(
    fetchPlacesLegacyList,
  )

  useEffect(() => {
    setFetchPlacesLegacyList(searchFormClicked && useLegacyDestinationSelector)
  }, [searchFormClicked])

  if (brand === 'hoseasons') {
    addBoatsToDestinationList(defaultConfig)
  }

  const searchForm = (stuck: boolean) => (
    <div
      onClick={() => {
        if (!searchFormClicked) {
          setSearchFormClicked(() => true)
        }
      }}
    >
      <SearchExperience
        formVariant={searchFormBetaEnabled ? 'experimental' : 'main'}
        environment={APP_ENV}
        brand={brand}
        locale={locale ?? ''}
        initialValues={
          {
            ...initialValues,
            destination: destinationLabel,
            placesId,
          } as SearchFormFields
        }
        isSummary={true}
        isSummaryFloating={!stuck}
        isGuestApp={isSearchFormInGuestApp}
        marketCode={marketCode}
        pageMeta={{
          pageType: 'search result',
          pageName: 'search result',
          pageSection: 'shop',
        }}
        translations={translations}
        smartSearch={{
          endpoint: '/api/search/smart-search',
          enabled: smartSearchEnabled,
        }}
        // The package now owns `destinationListConfig` (countries + boats gating
        // via `boatsFetch`, plus pageSize/pageSizePopular which default to 20/20 —
        // matching this MFE's historical `placesConfig` defaults).
        // NOTE(AS-2759): the package's `config.destinationListConfig` override
        // still requires a FULL `DestinationListConfig` (incl. `destinationFetch`),
        // so per-call sizing / `initialParentDestinationList` overrides can't be
        // deep-merged in isolation. This is currently inert: the package defaults
        // (pageSize/pageSizePopular 20/20) already match this MFE's historical
        // `placesConfig` defaults, and no caller overrides `initialParentDestinationList`
        // today. Revisit once the package accepts partial sizing overrides.
        config={{
          ...defaultConfig,
          autocompleteDebounceWait: 100,
        }}
        flags={{
          googleFallbackMethod,
          useLegacyDestinationSelector,
        }}
        tracking={{
          trackEvent: (eventKey, meta) => {
            // The exposure event must include both experiment arms while
            // excluding users who were never enrolled in Smart Search.
            if (
              eventKey.startsWith('smart-search-') &&
              !smartSearchExperimentEnabled
            ) {
              return
            }

            return trackClientSideOptimizelyEvent(
              OPTIMIZELY_EVENT_KEY_MAP[eventKey] ?? eventKey,
              optimizelyDecisionsContext,
              meta as EventTags,
            )
          },
          logWarning: (message) => {
            globalThis.newrelic?.noticeError(message)
          },
        }}
        boatsFetch={(fetchLocale, id) =>
          getHardcodedDestinationData(fetchLocale, id?.toString())
        }
        clientHeader={clientHeader}
        dataLayerErrorHandler={dataLayerErrorHandler}
        legacyListWithPlaces={{
          enabled: useLegacyDestinationSelector,
          data: placesLegacyListData,
        }}
        submitContext={{ ne, sw }}
      />
    </div>
  )

  const { displayMode, setDisplayMode } = useDisplayContext()
  const setDisplayModeWithTms = setDisplayModeWrapper(tms, setDisplayMode)
  const searchControls = () => {
    const sortOptionsFromConfig = getSortOptionsFromConfig(
      accommodationType,
      brand,
    )

    const displaySortOptions = shouldDisplaySortOptions({
      displayMode,
    })

    const sortOptions = sortOptionsFromConfig.map((option) => ({
      text: formatMessage({
        id: `search-controls.sort.option-names.${option}`,
      }),
      value: option,
    }))

    return (
      <SearchControls
        filtersCount={totalSelectedFilters?.toString() || '0'}
        sorting={
          displaySortOptions
            ? {
                sortOptions,
                labels: {
                  selectOption: formatMessage({
                    id: 'search-controls.sort.select-option',
                    defaultMessage: 'Select sort option',
                  }),
                  selectedOptionPrefix: formatMessage({
                    id: 'search-controls.sort.selected-option-prefix',
                    defaultMessage: 'Sort',
                  }),
                },
                selectedOption: sort ?? '',
                onOptionChange: (sort: string) =>
                  dispatch({ type: SORT_ORDER_UPDATED, sort }),
              }
            : undefined
        }
        showFilters={openFilters}
        justify={['space-evenly', 'space-evenly', 'space-evenly']}
        shouldHideSortDisplayText={true}
        displayMode={displayMode}
        setDisplayMode={setDisplayModeWithTms}
        translations={{
          filterTitle: formatMessage({
            id: 'search-controls.filter.title',
            defaultMessage: 'Filters',
          }),
          filterAriaText: formatMessage({
            id: 'search-controls.filter.aria',
            defaultMessage: 'Open filters',
          }),
          listTitle: formatMessage({
            id: 'search-controls.list.title',
            defaultMessage: 'Back to list view',
          }),
          listAriaText: formatMessage({
            id: 'search-controls.list.aria',
            defaultMessage: 'Back to list view',
          }),
          mapTitle: formatMessage({
            id: 'search-controls.map.title',
            defaultMessage: 'Map',
          }),
          mapAriaText: formatMessage({
            id: 'search-controls.map.aria',
            defaultMessage: 'Map View',
          }),
        }}
      />
    )
  }

  if (mobileStickyMode) {
    return (
      <MobileSticky isGuestApp={isSearchFormInGuestApp}>
        {({ stuck }) => (
          <div
            /*  GD-369 Hack - adding correct gradient for James Villas */
            id={
              brand === 'jamesVillas'
                ? 'james-villas-shadow-hack-wrapper-GD-369'
                : undefined
            }
          >
            <StickableHeaderExtended
              stuck={stuck}
              hasSeparator={!searchFormBetaEnabled}
            >
              <Container>{searchForm(stuck)}</Container>
            </StickableHeaderExtended>
            {showSearchControls && hasResults && (
              <MobileStickableContent stuck={stuck}>
                {searchControls()}
              </MobileStickableContent>
            )}
          </div>
        )}
      </MobileSticky>
    )
  }

  return (
    <div>
      <Container>{searchForm(true)}</Container>
      {showSearchControls && (hasResults || displayMode === 'MAP') && (
        <>{searchControls()}</>
      )}
    </div>
  )
}
